import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { of, Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { environment } from '../../environments/environment';
import { AuthService } from '../auth.service';
import { ApiService } from '../api.service';

@Injectable({
  providedIn: 'root'
})
export class HandleFileService {

  constructor(
    private authHttp: HttpClient,
    private auth: AuthService,
    private api: ApiService,
  ) { }

  mergeOptions(options?: HttpHeaders | any) {
    let defaultHeaders = new HttpHeaders();
    if (!options) {
      options = {};
    } else if (options.headers !== undefined || options.headers !== null) {
      defaultHeaders = options.headers;
    }
    const mockToken = environment.mockToken;
    const jwtToken = this.auth.getAuthToken();
    if (mockToken && mockToken !== '') {
      defaultHeaders = defaultHeaders.set('Authorization', 'Token ' + mockToken);
    } else if (jwtToken) {
      defaultHeaders = defaultHeaders.set('Authorization', 'JWT ' + jwtToken);
    }
    return {
      ...options,
      headers: defaultHeaders
    };
  }

  handleError(error: HttpErrorResponse | any) {
    let errMsg: string;
    if (error instanceof HttpErrorResponse) {
      errMsg = `${error.status} - ${error.statusText || ''} ${JSON.stringify(error.error)}`;
      // handle unauthorized requests. most likely by removing token and logging user out.
      if (error.status === 401 || error.status === 403) {
        this.auth.deleteToken();

        // only redirect if the 403 was not from getting the JWT
        const isAuthFailure = (error.url.indexOf('jwt-exchange') >= 0);
        console.log('is auth failure: ', isAuthFailure);
        if (!isAuthFailure) {
          this.auth.redirectToLoginUrl();
        }
      }
    } else {
      errMsg = error.message ? error.message : error.toString();
    }
    console.log('API Error: ', errMsg);
    return of({ error });
  }

  get(path: string, options?: any | null): Observable<any> {
    const headers = new HttpHeaders();
    if (!options) {
      options = {
        headers: headers,
        responseType: 'arrayBuffer'
      };
    }
    return this.authHttp.get(this.api.getPortwithURL(path), this.mergeOptions(options))
      .pipe(
        map((result) => {
          return result;
        }),
        catchError(this.handleError)
      );
      
  }

  post(path: string, body: any, options?: any, observeType: string = 'body'): Observable<any> {
    const headers = new HttpHeaders()
      .set('Content-Type', this.getFileType(body))
      .set('Content-Disposition', 'attachment; filename=template');

    if (!options) {
      options = {
        headers: headers,
        observe: observeType
      };
    }

    return this.authHttp.post(this.api.getPortwithURL(path), body, this.mergeOptions(options))
      .pipe(
        map((result) => {
          return result;
        }),
        catchError(this.handleError)
      );
  }

  getFileType(file): string {
    if (file.type.length > 0) {
      return file.type;
    } else {
      return this.getContentType(file.name);
    }
  }

  getContentType(fileName): string {
    const fileExt = fileName.slice(-4);
    let contentType;
    switch (fileExt) {
      case '.xls':
      case 'xlsx':
        contentType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
        break;
      case 'xlsm':
        contentType = 'application/vnd.ms-excel.sheet.macroEnabled.12';
        break;
      case '.csv':
        contentType = 'text/csv';
        break;
      case '.pdf':
        contentType = 'application/pdf';
        break;
      case '.doc':
        contentType = 'application/msword';
        break;
      case 'docx':
        contentType = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
        break;
      default:
        contentType = '';
        break;
    }
    return contentType;
  }
  
  download(path: string, body, options?: any): Observable<any> {
    const headers = new HttpHeaders();
    if (!options) {
      options = {
        headers: headers,
        responseType: 'arrayBuffer'
      };
    }
    return this.authHttp.post(this.api.getPortwithURL(path), body, this.mergeOptions(options))
      .pipe(
        map((result) => {
          return result;
        }),
        catchError(this.handleError)
      );
  }

}
